import { CloseTrigger, Drawer } from '@design/components/drawer/Drawer';
import {
  useGetScheduledQuery,
  useScheduledQueryPreview,
} from '@terminal/react-query-hooks/alfred/useScheduledQueries';
import {
  useCreateScheduledQuery,
  useUpdateScheduledQuery,
} from '@terminal/react-query-hooks/alfred/useScheduledQueryMutations';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useScheduleDrawerStore } from '@terminal/stores/use-schedule-drawer-store/useScheduleDrawerStore';
import { useScheduledQueryToast } from '@terminal/views/alfred/research/hooks/useScheduledQueryToast';
import {
  defaultPromptFormValue,
  isPromptFormDirty,
  PromptForm,
  PromptFormValue,
} from '@terminal/views/alfred/research/prompt-form/PromptForm';
import { useResolveWebSearch } from '@terminal/views/alfred/research/prompt-form/useResolveWebSearch';
import { buildCadence } from '@terminal/views/alfred/research/prompt-form/utils/buildCadence';
import { scheduledQueryToPromptFormValue } from '@terminal/views/alfred/research/prompt-form/utils/scheduledQueryToPromptFormValue';
import { storedWebSearchToUIValue } from '@terminal/views/alfred/research/prompt-form/utils/storedWebSearchToUIValue';
import { toRequestMentions } from '@terminal/views/alfred/research/prompt-form/utils/toRequestMentions';
import { ScheduledQueryDeliveryStatus } from '@terminal/views/alfred/research/scheduled-query-delivery-status/ScheduledQueryDeliveryStatus';
import { WebSearch } from '@terminal/views/alfred/research/services/conversations-service';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ScheduledQuerySettingsDrawer.module.scss';

const DEFAULT_ANALYSIS_MODE = 'Deep';

export interface ScheduledQuerySettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ScheduledQuerySettingsDrawer = ({
  isOpen,
  onClose,
}: ScheduledQuerySettingsDrawerProps) => {
  const { t } = useTranslation(['chat']);
  const isTasksUpdate = useFlags(state =>
    state.getFeatureFlagState('REASON-2703-scheduled-tasks-update')
  );
  const {
    editingTaskId: storeEditingTaskId,
    initialQuery,
    priorWebSearch,
    trackingSource,
  } = useScheduleDrawerStore(state => ({
    editingTaskId: state.editingTaskId,
    initialQuery: state.initialQuery,
    priorWebSearch: state.priorWebSearch,
    trackingSource: state.trackingSource,
  }));
  // The store clears editingTaskId on close while the drawer is still
  // animating out, which would flip it to create mode for a moment. The
  // drawer is remounted per open, so the value at mount is the right one.
  const [editingTaskId] = useState(storeEditingTaskId);
  const isEditMode = !!editingTaskId;
  const { trackScheduledQueryDrawer } = usePosthogEvents();

  // Lazy initializer picks up initialQuery at the moment the drawer key changes
  // (i.e. when open() or openForEdit() is called), so no useEffect is needed
  // to reset create-mode form state.
  const [formState, setFormState] = useState<PromptFormValue>(() => ({
    ...defaultPromptFormValue,
    scheduleEnabled: true,
    query: initialQuery ?? '',
  }));

  // Track the committed (saved/loaded) state so we can detect unsaved changes.
  const originalFormStateRef = useRef<PromptFormValue>({
    ...defaultPromptFormValue,
    scheduleEnabled: true,
    query: initialQuery ?? '',
  });

  const { data: editingTask } = useGetScheduledQuery(
    editingTaskId ?? '',
    isEditMode && isOpen
  );

  const [isFormReady, setIsFormReady] = useState(!isEditMode);

  // Preserve the task's stored timezone on edit
  const cadence = buildCadence(formState, editingTask?.cadence?.timezone);
  const { data: nextRun } = useScheduledQueryPreview(
    isTasksUpdate && isOpen && isFormReady && cadence
      ? { id: editingTaskId ?? undefined, cadence }
      : undefined
  );

  const { webSearch, setWebSearch } = useChat(state => ({
    webSearch: state.webSearch,
    setWebSearch: state.setWebSearch,
  }));
  // Snapshot the user's global web-search preference so we can restore it if
  // they cancel editing without saving.
  const priorWebSearchRef = useRef<WebSearch | null>(null);
  // Track the task's loaded web-search value separately from formState (it
  // lives in the global useChat store, not the drawer's local form state)
  // so edit-mode dirty checks below can detect web-search-only changes.
  const originalWebSearchRef = useRef<WebSearch | null>(null);

  // Pre-fill form when the task data arrives in edit mode (async fetch)
  useEffect(() => {
    if (isEditMode && editingTask) {
      const loaded = scheduledQueryToPromptFormValue(
        editingTask,
        editingTask.cadence != null
      );
      setFormState(loaded);
      setIsFormReady(true);
      originalFormStateRef.current = loaded;

      priorWebSearchRef.current = useChat.getState().webSearch;
      const loadedWebSearch = storedWebSearchToUIValue(
        editingTask.request.tools_config
      );
      originalWebSearchRef.current = loadedWebSearch;
      setWebSearch(loadedWebSearch);
    }
  }, [isEditMode, editingTask, setWebSearch]);

  const { mutate: createScheduledQuery, isPending: isCreating } =
    useCreateScheduledQuery();
  const { mutate: updateScheduledQuery, isPending: isUpdating } =
    useUpdateScheduledQuery();
  const isPending = isCreating || isUpdating;
  const { resolve: resolveWebSearch, isResolving } = useResolveWebSearch();
  const { showScheduledToast } = useScheduledQueryToast();

  const updateForm = (patch: Partial<PromptFormValue>) => {
    setFormState(prev => ({ ...prev, ...patch }));
  };

  const handleSave = async () => {
    const currentWebSearch = useChat.getState().webSearch;
    const resolvedToolsConfig = await resolveWebSearch(currentWebSearch);
    if (!resolvedToolsConfig) {
      return;
    }

    if (isEditMode && editingTaskId) {
      updateScheduledQuery(
        {
          id: editingTaskId,
          body: {
            title: formState.title.trim(),
            request: {
              message: formState.query.trim(),
              analysis_mode: DEFAULT_ANALYSIS_MODE,
              mentions: toRequestMentions(formState.mentions),
              tools_config: {
                ...editingTask?.request.tools_config,
                ...resolvedToolsConfig,
              },
            },
            ...(cadence != null && { cadence }),
          },
        },
        {
          onSuccess: () => {
            if (trackingSource && cadence) {
              trackScheduledQueryDrawer({
                action: 'edit',
                source: trackingSource,
                promptId: editingTaskId,
                cadence,
              });
            }
            restorePriorWebSearch();
            onClose();
            showScheduledToast(editingTaskId, 'updated');
          },
        }
      );
    } else {
      // Schedule drawer create always requires cadence
      if (!cadence) {
        return;
      }

      createScheduledQuery(
        {
          title: formState.title.trim(),
          request: {
            message: formState.query.trim(),
            analysis_mode: DEFAULT_ANALYSIS_MODE,
            mentions: toRequestMentions(formState.mentions),
            tools_config: resolvedToolsConfig,
          },
          cadence,
        },
        {
          onSuccess: data => {
            if (trackingSource) {
              trackScheduledQueryDrawer({
                action: 'save',
                source: trackingSource,
                promptId: data.id,
                cadence,
              });
            }
            restorePriorWebSearch();
            onClose();
            showScheduledToast(data.id, 'created');
          },
        }
      );
    }
  };

  const restorePriorWebSearch = () => {
    if (priorWebSearchRef.current !== null) {
      setWebSearch(priorWebSearchRef.current);
      priorWebSearchRef.current = null;
    } else if (priorWebSearch !== null) {
      setWebSearch(priorWebSearch);
    }
  };

  const handleCancel = () => {
    restorePriorWebSearch();
    onClose();
  };

  const isDirty =
    isPromptFormDirty(formState, originalFormStateRef.current) ||
    (isEditMode && webSearch !== originalWebSearchRef.current);

  const closeTriggers = isDirty
    ? [CloseTrigger.CloseButtonClick, CloseTrigger.EscapeKey]
    : [
        CloseTrigger.CloseButtonClick,
        CloseTrigger.EscapeKey,
        CloseTrigger.BackgroundClick,
      ];

  const drawerTitle = isEditMode
    ? t('chat:scheduledQuerySettings.editDrawerTitle')
    : t('chat:scheduledQuerySettings.drawerTitle');

  const description = isEditMode
    ? t('chat:scheduledQuerySettings.editDescription')
    : t('chat:scheduledQuerySettings.description', {
        context: isTasksUpdate ? 'task' : undefined,
      });

  const submitLabel = isEditMode
    ? t('chat:scheduledQuerySettings.updateTask')
    : t('chat:scheduledQuerySettings.saveTask');

  return (
    <Drawer
      isOpen={isOpen}
      handleCloseClick={handleCancel}
      closeTriggers={closeTriggers}
      className={styles.root}
      data-testid="scheduled-query-settings-drawer"
    >
      <div className={styles.container}>
        <div className={styles.content}>
          <h2 className={styles.drawerTitle}>{drawerTitle}</h2>
          <p className={styles.description}>{description}</p>
          <ScheduledQueryDeliveryStatus openInNewTab />

          <PromptForm
            testId="scheduled-query-drawer"
            value={formState}
            onChange={updateForm}
            showScheduleToggle={false}
            bodyLabel={t('chat:scheduledQuerySettings.query')}
            showPlaceholder
            onSubmit={handleSave}
            onCancel={handleCancel}
            submitLabel={submitLabel}
            isSubmitting={isPending || isResolving}
            isDirty={isEditMode ? isDirty : undefined}
            nextRun={nextRun}
          />
        </div>
      </div>
    </Drawer>
  );
};
